if (!customElements.get("drawer-picker")) {
  customElements.define(
    "drawer-picker",
    class DrawerPicker extends HTMLElement {
      constructor() {
        super();
        this.duetPicker = this.querySelector("duet-date-picker");
        this.dataJSON = this.querySelector("[data-json]");
        this.actButton = document.querySelector(
          "#CartDrawer-Checkout",
        );
        this.checkInterval = null;
        this.minOffsetDays = Number.parseInt(this.dataset.minOffset || "0", 10);
        if (Number.isNaN(this.minOffsetDays)) {
          this.minOffsetDays = 0;
        }

        // Set localization on the picker using data attributes
        this.setLocalization();

        // Determine the earliest selectable date
        this.setMinSelectableDate();

        // Set up date disabled function
        this.duetPicker.isDateDisabled = (date) => this.isDateDisabled(date);

        // Initial check and set up interval to monitor button state
        this.setupButtonStateCheck();

        this.initEventListeners();

        // Setup input click handlers after duet-date-picker is ready
        this.setupInputClickHandlers();

        this.updateButtonState();

      }

      // Set localization for the date picker
      setLocalization() {
        if (this.duetPicker) {
          this.duetPicker.localization = {
            buttonLabel: this.dataset.buttonLabel || "Datum wählen",
            placeholder: this.dataset.placeholder || "tt.mm.jjjj",
            selectedDateMessage:
              this.dataset.selectedDateMessage || "Ausgewähltes Datum ist",
            prevMonthLabel: this.dataset.prevMonthLabel || "Vorheriger Monat",
            nextMonthLabel: this.dataset.nextMonthLabel || "Nächster Monat",
            monthSelectLabel: this.dataset.monthSelectLabel || "Monat",
            yearSelectLabel: this.dataset.yearSelectLabel || "Jahr",
            closeLabel: this.dataset.closeLabel || "Fenster schließen",
            calendarHeading:
              this.dataset.calendarHeading || "Wählen Sie ein Datum",
            dayNames: this.dataset.dayNames
              ? this.dataset.dayNames.split(",")
              : [
                "Sonntag",
                "Montag",
                "Dienstag",
                "Mittwoch",
                "Donnerstag",
                "Freitag",
                "Samstag",
              ],
            monthNames: this.dataset.monthNames
              ? this.dataset.monthNames.split(",")
              : [
                "Januar",
                "Februar",
                "März",
                "April",
                "Mai",
                "Juni",
                "Juli",
                "August",
                "September",
                "Oktober",
                "November",
                "Dezember",
              ],
            monthNamesShort: this.dataset.monthNamesShort
              ? this.dataset.monthNamesShort.split(",")
              : [
                "Jan",
                "Feb",
                "Mär",
                "Apr",
                "Mai",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Okt",
                "Nov",
                "Dez",
              ],
            locale: this.dataset.locale || "de-DE",
          };

          const DATE_FORMAT = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

          this.duetPicker.dateAdapter = {
            parse(value = "", createDate) {
              const matches = value.match(DATE_FORMAT);
              if (matches) {
                return createDate(matches[3], matches[2], matches[1]);
              }
            },
            format(date) {
              return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
            },
          };
        }
      }

      // Set up button state checking
      setupButtonStateCheck() {
        if (this.actButton) {
          this.actButton.setAttribute("disabled", "true");

          // Set up interval to check if button should be disabled
          this.checkInterval = setInterval(() => {
            if (!this.duetPicker.value) {
              this.actButton.setAttribute("disabled", "true");
            }
          }, 400);
        }
      }

      // Initialize event listeners
      initEventListeners() {
        this.duetPicker.addEventListener("duetChange", (event) => {
          this.handleDateChange(event.detail.valueAsDate);
          this.updateButtonState();
        });

        const observer = new MutationObserver((mutationsList, observer) => {
          const datePickerInput = this.querySelector(".duet-date__input");

          if (datePickerInput) {
            datePickerInput.addEventListener("click", (e) => {
              e.preventDefault();
              this.duetPicker.show().then(() => {
                setTimeout(() => {
                  document.activeElement.blur();
                }, 350);
              });
            });
            observer.disconnect();
          }
        });

        // 5. Start observing the container for child element additions
        observer.observe(this, { childList: true, subtree: true });
      }

      // Setup input click handlers to open picker
      setupInputClickHandlers() {
        customElements.whenDefined("duet-date-picker").then(() => {
          setTimeout(() => {
            const inputElement = this.duetPicker.querySelector("input");
            if (inputElement) {
              inputElement.addEventListener("click", (e) => {
                if (
                  this.duetPicker &&
                  typeof this.duetPicker.show === "function"
                ) {
                  this.duetPicker.show();
                }
              });
            }
          }, 300);
        });
      }

      // Handle date selection and validation
      handleDateChange(selectedDate) {
        if (!selectedDate) return;

        const dateString = this.formatDateForAPI(selectedDate);
        console.log("Selected date:", this.formatDate(dateString));
        this.updateCartWithDeliveryDate(dateString);
      }

      // Check if date should be disabled (past dates + blacklisted dates)
      isDateDisabled(date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const minDateBaseline =
          this.enforcedMinDate && this.parseISODateString(this.enforcedMinDate)
            ? this.parseISODateString(this.enforcedMinDate)
            : today;

        if (minDateBaseline && date < minDateBaseline) {
          return true;
        }

        // Check blacklisted dates
        const blacklistedDates = this.dataJSON?.textContent;
        if (blacklistedDates) {
          try {
            const dateArray = JSON.parse(blacklistedDates);
            const dateString = this.formatDateForAPI(date);
            if (dateArray.includes(dateString)) {
              console.log("Date disabled:", this.formatDate(dateString));
              return true;
            }
          } catch (error) {
            console.error("Error parsing blacklisted dates:", error);
          }
        }

        return false;
      }

      // Show alert for blacklisted dates
      showBlacklistAlert() {
        alert(
          this.dataset.alert ||
          "Das gewählte Datum liegt in einem gesperrten Zeitraum. Bitte wählen Sie ein anderes Datum.",
        );
      }

      // Update button state based on picker value
      updateButtonState() {
        if (this.duetPicker.value) {
          if (this.actButton) {
            this.actButton.removeAttribute("disabled");
            this.actButton.textContent =
              this.dataset.textActionEnabled;
            // Clear interval when we have a value
            if (this.checkInterval) {
              clearInterval(this.checkInterval);
              this.checkInterval = null;
            }
          }
        } else {
          if (this.actButton) {
            this.actButton.setAttribute("disabled", "true");
            this.actButton.textContent =
              this.dataset.textActionDisabled;
            // Restart interval when input becomes empty
            if (!this.checkInterval) {
              this.setupButtonStateCheck();
            }
          }
        }
      }

      // Format date for display (DD-MM-YYYY)
      formatDate(dateString) {
        if (!dateString) return "";
        const date = new Date(dateString + "T00:00:00");
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }

      // Format date for API (YYYY-MM-DD)
      formatDateForAPI(date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
      }

      setMinSelectableDate() {
        if (!this.duetPicker) return;

        const originalMin = this.duetPicker.getAttribute("min");
        const offsetMin =
          Number.isFinite(this.minOffsetDays) && this.minOffsetDays > 0
            ? this.getOffsetDateString(this.minOffsetDays)
            : null;

        const candidates = [originalMin, offsetMin].filter(Boolean);
        if (!candidates.length) return;

        const minDate = candidates.reduce((latest, current) => {
          const currentDate = this.parseISODateString(current);
          if (!currentDate) return latest;
          if (!latest) return current;
          const latestDate = this.parseISODateString(latest);
          if (!latestDate) return current;
          return currentDate > latestDate ? current : latest;
        }, null);

        if (minDate) {
          this.enforcedMinDate = minDate;
          this.duetPicker.setAttribute("min", minDate);
          this.duetPicker.min = minDate;
        }
      }

      getOffsetDateString(days) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const targetDate = new Date(today);
        targetDate.setDate(targetDate.getDate() + days);
        return this.formatDateForAPI(targetDate);
      }

      parseISODateString(value) {
        if (!value) return null;
        const parts = value.split("-");
        if (parts.length !== 3) return null;
        const [yearPart, monthPart, dayPart] = parts;
        const year = Number.parseInt(yearPart, 10);
        const month = Number.parseInt(monthPart, 10);
        const day = Number.parseInt(dayPart, 10);
        if (
          Number.isNaN(year) ||
          Number.isNaN(month) ||
          Number.isNaN(day)
        ) {
          return null;
        }
        return new Date(year, month - 1, day);
      }

      // Clear the picker selection
      clearSelection() {
        this.duetPicker.value = "";
        this.updateButtonState();
      }

      // Update cart with selected delivery date
      updateCartWithDeliveryDate(selectedDate) {
        const formData = new FormData();
        const formattedDate = this.formatDate(selectedDate);
        formData.append("attributes[Liefertermin]", formattedDate);

        fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => console.log("Cart updated:", data))
          .catch((error) => console.error("Error updating cart:", error));
      }
    },
  );
}
